import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import type { ConversationListEntry, Message } from '../types';
import { useChat } from '../store';
import { api, ApiError } from '../api';
import { getSocket } from '../socket';
import { formatMessageBody } from '../utils/messageFormat';
import { timeOfDay } from '../utils/time';
import { confirmDialog } from './ConfirmHost';
import { toast } from './ToastHost';
import { AttachmentRenderer } from './AttachmentRenderer';
import {
  IconCheck,
  IconCheckDouble,
  IconForward,
  IconPencil,
  IconPin,
  IconReply,
  IconSmile,
  IconStar,
  IconTrash,
} from './Icon';

// Forward modal is a sizable component (conv picker + filtering) that few
// users open per session, so we lazy-load it. The first click pays the
// fetch; subsequent opens are cached.
const ForwardModalLazy = lazy(() =>
  import('./ForwardModal').then((m) => ({ default: m.ForwardModal })),
);

interface MessageBubbleProps {
  message: Message;
  showSender: boolean;
  senderName: string | null;
  isMine: boolean;
}

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

// Hide the body bubble when it's the auto-generated `[filename]` placeholder
// the server inserts for media-only uploads.
function hideBodyForAttachment(message: Message): boolean {
  if (!message.attachments || message.attachments.length === 0) return false;
  if (!message.body) return true;
  return message.attachments.some((a) => message.body === `[${a.filename}]`);
}

export function MessageBubble({ message, showSender, senderName, isMine }: MessageBubbleProps) {
  const me = useChat((s) => s.me);
  const setReplyingTo = useChat((s) => s.setReplyingTo);
  const isStarred = useChat((s) => Boolean(s.starredMessageIds[message.id]));
  const toggleStarLocal = useChat((s) => s.toggleStarLocal);
  const isPinned = useChat((s) => Boolean(s.pinnedMessageIds[message.id]));
  const togglePinLocal = useChat((s) => s.togglePinLocal);
  const conversation = useChat((s) =>
    s.conversations.find((c) => c.id === message.conversationId),
  );
  const messages = useChat((s) => s.messagesByConv[message.conversationId]) ?? [];
  // For DM read-receipts: the other user's last-read message id, if any.
  // The server only broadcasts read events when the user has read receipts
  // turned on, so 'undefined' here naturally means "no signal" — render
  // the single tick.
  const otherLastReadId = useChat((s) => {
    const conv = s.conversations.find((c) => c.id === message.conversationId);
    if (!conv || conv.type !== 'dm') return undefined;
    const otherId = conv.participants.find((p) => p.id !== s.me?.id)?.id;
    if (!otherId) return undefined;
    return s.readByConv[message.conversationId]?.[otherId];
  });

  // For group read-receipts: the per-user lastReadId map for this
  // conversation. Used to compute "read by N of M" on my outgoing group
  // messages. Shape: { [userId]: lastReadMessageId }. Only present when
  // at least one other member has emitted a read event with receipts on.
  const groupReadMap = useChat((s) => {
    const conv = s.conversations.find((c) => c.id === message.conversationId);
    if (!conv || conv.type === 'dm') return undefined;
    return s.readByConv[message.conversationId];
  });
  const parentMessage = message.parentId
    ? messages.find((m) => m.id === message.parentId) ?? null
    : null;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.body ?? '');
  const [showActions, setShowActions] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  const editRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing) {
      editRef.current?.focus();
      editRef.current?.setSelectionRange(draft.length, draft.length);
    }
  }, [editing, draft.length]);

  async function saveEdit() {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === message.body) {
      setEditing(false);
      setDraft(message.body ?? '');
      return;
    }
    try {
      await api.editMessage(message.id, trimmed);
      // Server emits message:edited which the socket handler upserts in store.
    } catch {
      /* ignore — UI will revert on next render */
    }
    setEditing(false);
  }

  async function handleDelete() {
    const ok = await confirmDialog({
      title: 'Delete this message?',
      description: 'It will be removed for everyone in this chat. This cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.deleteMessage(message.id);
    } catch {
      /* ignore */
    }
  }

  async function toggleStar() {
    const next = !isStarred;
    toggleStarLocal(message.id, next);
    try {
      if (next) await api.starMessage(message.id);
      else await api.unstarMessage(message.id);
    } catch {
      // Revert optimistic update if the server rejects.
      toggleStarLocal(message.id, !next);
    }
  }

  async function togglePin() {
    const next = !isPinned;
    togglePinLocal(message.id, next);
    try {
      if (next) await api.pinMessage(message.id);
      else await api.unpinMessage(message.id);
    } catch (err) {
      // Revert optimistic update if the server rejects (e.g. cap reached
      // when pinning a 4th message). Surface the specific failure mode
      // — a silent revert leaves the user wondering why the action
      // didn't take.
      togglePinLocal(message.id, !next);
      if (err instanceof ApiError && err.code === 'pin_cap_reached') {
        toast('This chat already has 3 pinned messages — unpin one first.', 'error');
      } else if (next) {
        toast("Couldn't pin that message.", 'error');
      } else {
        toast("Couldn't unpin that message.", 'error');
      }
    }
  }

  // Click sender name OR an @mention → open or create a DM with that
  // user. Same flow as NewChatModal.startDm: idempotent on the server,
  // stub the entry locally if it's brand new, join the WS room, then
  // select.
  async function openDmWithSender(senderId: string) {
    if (senderId === me?.id) return; // can't DM self
    const state = useChat.getState();
    // Look for an existing 1:1 DM with this person and just select it.
    const existing = state.conversations.find(
      (c) =>
        c.type === 'dm' &&
        c.participants.length === 2 &&
        c.participants.some((p) => p.id === senderId),
    );
    if (existing) {
      state.selectConversation(existing.id);
      return;
    }
    try {
      const res = await api.createDm(senderId);
      if (!state.conversations.find((c) => c.id === res.conversation.id)) {
        const stub: ConversationListEntry = {
          ...res.conversation,
          lastMessage: null,
          unreadCount: 0,
          lastReadId: null,
          myRole: 'room_admin',
          isFavorite: false,
          archivedAt: null,
          mutedUntil: null,
        channelId: null,
        };
        useChat.getState().upsertConversation(stub);
      }
      getSocket().emit('conversation:join', { conversationId: res.conversation.id });
      useChat.getState().selectConversation(res.conversation.id);
    } catch {
      /* swallowed — opening a DM is a soft, optional action */
    }
  }

  async function toggleReaction(emoji: string) {
    if (!me) return;
    const reaction = (message.reactions ?? []).find((r) => r.emoji === emoji);
    const youReacted = reaction?.userIds.includes(me.id) ?? false;
    try {
      if (youReacted) {
        await api.removeReaction(message.id, emoji);
      } else {
        await api.addReaction(message.id, emoji);
      }
    } catch {
      /* ignore */
    }
    setShowReactions(false);
  }

  const isDeleted = message.deletedAt !== null;
  const reactions = message.reactions ?? [];

  // Right-click context menu — parallels the conversation-row pattern.
  // Touch-only users (who can't hover the action bar) and desktop power
  // users both get a single-gesture path to the same actions.
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  useEffect(() => {
    if (!menuPos) return;
    const close = () => setMenuPos(null);
    window.addEventListener('click', close);
    window.addEventListener('contextmenu', close);
    window.addEventListener('blur', close);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('contextmenu', close);
      window.removeEventListener('blur', close);
    };
  }, [menuPos]);

  return (
    <div
      className={`group flex ${isMine ? 'justify-end' : 'justify-start'} px-4 py-1`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => {
        setShowActions(false);
        setShowReactions(false);
      }}
      onContextMenu={(e) => {
        if (isDeleted || editing) return;
        e.preventDefault();
        setMenuPos({ x: e.clientX, y: e.clientY });
      }}
    >
      <div className={`max-w-xl flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
        {showSender && !isMine && senderName && (
          // Group sender names are clickable — open or create a DM with
          // that member. Skipped for the user themselves (can't DM self;
          // also `!isMine` already prevents that branch from running).
          <button
            onClick={() => void openDmWithSender(message.senderId)}
            className="text-[11px] text-zinc-400 hover:text-zinc-200 mb-0.5 px-1 transition-colors"
            title={`Open DM with ${senderName}`}
          >
            {senderName}
          </button>
        )}

        <div className="flex items-end gap-2">
          {!isMine && <div className="flex-1" />}

          {/* Action menu */}
          {showActions && !isDeleted && !editing && (
            <div
              className={`flex items-center gap-0.5 glass-chrome hairline rounded-full px-1 py-1 animate-scale-in ${
                isMine ? 'order-1' : 'order-2'
              }`}
            >
              <div className="relative">
                <button
                  onClick={() => setShowReactions((v) => !v)}
                  className="p-1 rounded hover:bg-zinc-800 text-zinc-400 transition"
                  title="React"
                  aria-label="Add reaction"
                >
                  <IconSmile size={14} />
                </button>
                {showReactions && (
                  <div
                    className={`absolute z-10 ${
                      isMine ? 'right-0' : 'left-0'
                    } bottom-full mb-1 flex bg-zinc-900 border border-zinc-800 rounded-md p-1 shadow-lg`}
                  >
                    {QUICK_REACTIONS.map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => toggleReaction(emoji)}
                        className="px-1.5 py-0.5 hover:bg-zinc-800 rounded text-base"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => setReplyingTo(message.conversationId, message)}
                className="p-1 rounded hover:bg-zinc-800 text-zinc-400 transition"
                title="Reply"
                aria-label="Reply to message"
              >
                <IconReply size={14} />
              </button>
              <button
                onClick={() => setForwardOpen(true)}
                className="p-1 rounded hover:bg-zinc-800 text-zinc-400 transition"
                title="Forward"
                aria-label="Forward message"
              >
                <IconForward size={14} />
              </button>
              <button
                onClick={toggleStar}
                className={`p-1 rounded hover:bg-zinc-800 transition ${
                  isStarred ? 'text-amber-400' : 'text-zinc-400'
                }`}
                title={isStarred ? 'Unstar' : 'Star'}
                aria-label={isStarred ? 'Unstar message' : 'Star message'}
              >
                <IconStar size={14} filled={isStarred} />
              </button>
              <button
                onClick={togglePin}
                className={`p-1 rounded hover:bg-zinc-800 transition ${
                  isPinned ? 'text-amber-400' : 'text-zinc-400'
                }`}
                title={isPinned ? 'Unpin from chat' : 'Pin to chat'}
                aria-label={isPinned ? 'Unpin from chat' : 'Pin to chat'}
              >
                <IconPin size={14} filled={isPinned} />
              </button>
              {isMine && (
                <>
                  <button
                    onClick={() => {
                      setDraft(message.body ?? '');
                      setEditing(true);
                    }}
                    className="p-1 rounded hover:bg-zinc-800 text-zinc-400 transition"
                    title="Edit"
                    aria-label="Edit message"
                  >
                    <IconPencil size={14} />
                  </button>
                  <button
                    onClick={handleDelete}
                    className="p-1 rounded hover:bg-zinc-800 text-rose-400 transition"
                    title="Delete"
                    aria-label="Delete message"
                  >
                    <IconTrash size={14} />
                  </button>
                </>
              )}
            </div>
          )}

          <div className={`${isMine ? 'order-2' : 'order-1'} flex flex-col gap-1.5`}>
            {/* "Forwarded" pill — small disclosure that this message
                originated elsewhere. Renders above the bubble like
                WhatsApp's, with a slightly subdued color so it doesn't
                compete with the content. */}
            {message.forwardedFromMessageId && !isDeleted && (
              <div
                className={`flex items-center gap-1 text-[10px] text-zinc-500 italic ${
                  isMine ? 'justify-end' : 'justify-start'
                }`}
              >
                <IconForward size={10} className="opacity-70" />
                <span>Forwarded</span>
              </div>
            )}
            {/* Quoted reply (parent message) */}
            {parentMessage && !isDeleted && (
              // Clicking the quoted parent jumps the message list to it —
              // same setJumpToMessage flow used by search results and the
              // pinned strip. Useful when a thread has scrolled the
              // original out of view.
              <button
                type="button"
                onClick={() =>
                  useChat.getState().setJumpToMessage(parentMessage.id)
                }
                className={`text-xs px-3 py-1.5 rounded-md border-l-2 text-left transition hover:opacity-100 ${
                  isMine
                    ? 'border-l-indigo-300 bg-indigo-500/10 text-indigo-100 hover:bg-indigo-500/15'
                    : 'border-l-zinc-500 bg-zinc-900 text-zinc-300 hover:bg-zinc-800'
                } max-w-md`}
                title="Jump to original message"
              >
                <p className="font-medium text-[11px] opacity-80 mb-0.5">
                  {(() => {
                    const sender = conversation?.participants.find(
                      (p) => p.id === parentMessage.senderId,
                    );
                    if (parentMessage.senderId === me?.id) return 'You';
                    return sender?.displayName ?? sender?.email ?? 'Unknown';
                  })()}
                </p>
                <p className="truncate opacity-80">
                  {parentMessage.deletedAt
                    ? <em>(deleted)</em>
                    : parentMessage.body ?? <em>(media)</em>}
                </p>
              </button>
            )}

            {/* Attachments first (above the bubble) */}
            {!isDeleted && message.attachments && message.attachments.length > 0 && (
              <div className={`flex flex-col gap-1.5 ${isMine ? 'items-end' : 'items-start'}`}>
                {message.attachments.map((att) => (
                  <AttachmentRenderer key={att.id} attachment={att} />
                ))}
              </div>
            )}

            {/* Body bubble — hidden if it's the placeholder caption for a media-only message */}
            {!hideBodyForAttachment(message) && (
              <div
                className={`rounded-[1.25rem] px-3.5 py-2 chat-message-text transition-colors duration-300 ease-swift ${
                  isDeleted
                    ? 'bg-zinc-900/60 text-zinc-500 italic hairline'
                    : isMine
                      ? 'bg-gradient-to-br from-indigo-500 to-indigo-600 text-white rounded-br-md shadow-inset-hairline-strong'
                      : 'bg-zinc-900 text-zinc-100 rounded-bl-md hairline shadow-inset-hairline'
                }`}
              >
                {isDeleted ? (
                  <span>Message deleted</span>
                ) : editing ? (
                  <textarea
                    ref={editRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        void saveEdit();
                      } else if (e.key === 'Escape') {
                        setEditing(false);
                        setDraft(message.body ?? '');
                      }
                    }}
                    onBlur={saveEdit}
                    rows={Math.min(6, Math.max(1, draft.split('\n').length))}
                    className="w-full bg-transparent border-none outline-none resize-none text-white min-w-[200px]"
                  />
                ) : (
                  // formatMessageBody autolinks URLs, renders the
                  // *bold* / _italic_ / ~strike~ / `code` shortcuts, and
                  // turns @mentions into clickable buttons that open a
                  // DM with the mentioned user. The wrapping <p> keeps
                  // whitespace + line breaks intact since the formatter
                  // is inline-only.
                  <p className="whitespace-pre-wrap break-words">
                    {formatMessageBody(message.body, {
                      // Resolve a handle string to a participant in this
                      // conversation. Mirrors the composer's handleFor:
                      // prefer username, fall back to email-local-part.
                      // Anything not matching a participant falls
                      // through as plain text.
                      resolveHandle: (handle) => {
                        if (!conversation) return null;
                        const lc = handle.toLowerCase();
                        const match = conversation.participants.find((p) => {
                          if (p.id === me?.id && (me?.email ?? '').toLowerCase().split('@')[0] === lc)
                            return true;
                          // The participant DTO doesn't carry username,
                          // so we fall back to the workspace users
                          // store for that lookup.
                          return p.email.toLowerCase().split('@')[0] === lc;
                        });
                        if (match) {
                          return { id: match.id, isMe: match.id === me?.id };
                        }
                        // Username path — check the workspace-users
                        // store (carries username) and confirm the
                        // matched user is in this conversation.
                        const state = useChat.getState();
                        const u = state.users.find(
                          (x) => (x.username ?? '').toLowerCase() === lc,
                        );
                        if (
                          u &&
                          conversation.participants.some((p) => p.id === u.id)
                        ) {
                          return { id: u.id, isMe: u.id === me?.id };
                        }
                        return null;
                      },
                      onMentionClick: (id) => void openDmWithSender(id),
                    })}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Reactions */}
        {reactions.length > 0 && !isDeleted && (
          <div className={`flex flex-wrap gap-1 mt-1 ${isMine ? 'justify-end' : 'justify-start'}`}>
            {reactions.map((r) => {
              const youReacted = me ? r.userIds.includes(me.id) : false;
              // Build a "who reacted" tooltip from the conversation's
              // participants. WhatsApp shows this in a popover; we
              // surface it via title attribute for now — cheap, works
              // on hover, and doesn't add a new positioned popover.
              const names = r.userIds
                .map((uid) => {
                  if (uid === me?.id) return 'You';
                  const p = conversation?.participants.find((x) => x.id === uid);
                  return p?.displayName ?? p?.email?.split('@')[0] ?? 'Someone';
                })
                .join(', ');
              return (
                <button
                  key={r.emoji}
                  onClick={() => toggleReaction(r.emoji)}
                  title={names ? `${names} reacted with ${r.emoji}` : undefined}
                  className={`text-xs px-1.5 py-0.5 rounded-full border transition ${
                    youReacted
                      ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-200'
                      : 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700'
                  }`}
                >
                  {r.emoji} {r.userIds.length}
                </button>
              );
            })}
          </div>
        )}

        {/* Time + edited marker + read receipt (for DMs only, my messages
            only). The double-tick turns blue once the recipient's last
            read pointer has reached or passed this message; otherwise we
            show a neutral single tick. */}
        <div className="text-[10px] text-zinc-500 mt-0.5 px-1 flex items-center gap-1">
          <span>{timeOfDay(message.createdAt)}</span>
          {message.editedAt && !isDeleted && <span>· edited</span>}
          {isMine && !isDeleted && conversation?.type === 'dm' && (
            <ReadReceiptTicks
              messageId={message.id}
              otherLastReadId={otherLastReadId ?? null}
              messages={messages}
            />
          )}
          {isMine && !isDeleted && conversation && conversation.type !== 'dm' && (
            <GroupReadCount
              messageId={message.id}
              meId={me?.id ?? null}
              participants={conversation.participants}
              readMap={groupReadMap}
              messages={messages}
            />
          )}
        </div>
      </div>
      {forwardOpen && (
        <Suspense fallback={null}>
          <ForwardModalLazy
            message={message}
            onClose={() => setForwardOpen(false)}
          />
        </Suspense>
      )}
      {menuPos && (
        // Right-click menu. Mirrors the conversation-row context menu
        // pattern from ConversationListItem. onClick stop-propagation
        // keeps an inner button click from triggering the global close
        // before its handler runs.
        <div
          role="menu"
          onClick={(e) => e.stopPropagation()}
          className="fixed z-50 min-w-[10rem] glass-chrome hairline rounded-xl shadow-elevation overflow-hidden animate-scale-in"
          style={{ left: menuPos.x, top: menuPos.y }}
        >
          <ContextMenuItem
            icon={<IconReply size={13} className="text-zinc-400" />}
            label="Reply"
            onClick={() => {
              setReplyingTo(message.conversationId, message);
              setMenuPos(null);
            }}
          />
          <ContextMenuItem
            icon={<IconForward size={13} className="text-zinc-400" />}
            label="Forward"
            onClick={() => {
              setForwardOpen(true);
              setMenuPos(null);
            }}
          />
          <ContextMenuItem
            icon={
              <IconStar
                size={13}
                className={isStarred ? 'text-amber-400' : 'text-zinc-400'}
                filled={isStarred}
              />
            }
            label={isStarred ? 'Unstar' : 'Star'}
            onClick={() => {
              void toggleStar();
              setMenuPos(null);
            }}
          />
          <ContextMenuItem
            icon={
              <IconPin
                size={13}
                className={isPinned ? 'text-amber-400' : 'text-zinc-400'}
                filled={isPinned}
              />
            }
            label={isPinned ? 'Unpin from chat' : 'Pin to chat'}
            onClick={() => {
              void togglePin();
              setMenuPos(null);
            }}
          />
          {isMine && (
            <>
              <ContextMenuItem
                icon={<IconPencil size={13} className="text-zinc-400" />}
                label="Edit"
                onClick={() => {
                  setDraft(message.body ?? '');
                  setEditing(true);
                  setMenuPos(null);
                }}
              />
              <ContextMenuItem
                icon={<IconTrash size={13} className="text-rose-400" />}
                label="Delete"
                destructive
                onClick={() => {
                  void handleDelete();
                  setMenuPos(null);
                }}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Single row inside the right-click menu. Kept tiny — the menu items
// are simple and the styling is shared with the conv-row context menu.
function ContextMenuItem({
  icon,
  label,
  onClick,
  destructive,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  destructive?: boolean;
}) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-xs transition-colors duration-150 ${
        destructive
          ? 'text-zinc-200 hover:bg-rose-500/10 hover:text-rose-300'
          : 'text-zinc-200 hover:bg-zinc-800'
      }`}
    >
      <span className="shrink-0">{icon}</span>
      <span className="flex-1">{label}</span>
    </button>
  );
}

// "Read by N" indicator for outgoing group messages. We count the other
// members whose lastReadId is at or beyond this message in the visible
// window. When everyone's read it (N = total others), we use the same
// blue double-check from the DM case so users get a consistent "fully
// seen" visual. Members who've opted out of read receipts simply never
// contribute a signal — they're counted as "not yet read" forever, but
// without a separate indicator that would expose the privacy choice.
function GroupReadCount({
  messageId,
  meId,
  participants,
  readMap,
  messages,
}: {
  messageId: string;
  meId: string | null;
  participants: Array<{ id: string }>;
  readMap: Record<string, string> | undefined;
  messages: Message[];
}) {
  const others = participants.filter((p) => p.id !== meId);
  const totalOthers = others.length;
  if (totalOthers === 0 || !readMap) {
    // No other members yet (just-created group) or no one has read past
    // anything yet — render a neutral single tick to indicate "sent".
    return (
      <span aria-label="Sent" title="Sent">
        <IconCheck size={10} className="opacity-70" />
      </span>
    );
  }
  const myIdx = messages.findIndex((m) => m.id === messageId);
  let readCount = 0;
  for (const other of others) {
    const theirLastReadId = readMap[other.id];
    if (!theirLastReadId) continue;
    const otherIdx = messages.findIndex((m) => m.id === theirLastReadId);
    // Same fallback as the DM case: if the message is older than the
    // visible window we optimistically count it as read.
    if (myIdx === -1 || otherIdx === -1 || otherIdx >= myIdx) readCount += 1;
  }
  if (readCount === 0) {
    return (
      <span aria-label="Sent" title="Sent">
        <IconCheck size={10} className="opacity-70" />
      </span>
    );
  }
  if (readCount >= totalOthers) {
    return (
      <span aria-label="Read by everyone" title="Read by everyone" className="text-sky-400">
        <IconCheckDouble size={12} />
      </span>
    );
  }
  return (
    <span
      aria-label={`Read by ${readCount} of ${totalOthers}`}
      title={`Read by ${readCount} of ${totalOthers}`}
      className="flex items-center gap-0.5"
    >
      <IconCheckDouble size={12} className="opacity-70" />
      <span className="tabular-nums text-[9px] text-zinc-400">
        {readCount}/{totalOthers}
      </span>
    </span>
  );
}

// Position-based comparison: message ids are UUIDs, so we walk the
// in-memory message list to decide whether the recipient's last-read
// pointer is at or after this message. The list is small (paginated
// pages of 50) so a linear find is fine. When we don't yet have the
// other user's read signal we fall back to the single-tick "sent" state.
function ReadReceiptTicks({
  messageId,
  otherLastReadId,
  messages,
}: {
  messageId: string;
  otherLastReadId: string | null;
  messages: Message[];
}) {
  // No signal — single tick. Either the recipient hasn't read, or has
  // read receipts off; both render the same way.
  if (!otherLastReadId) {
    return (
      <span aria-label="Sent" title="Sent">
        <IconCheck size={10} className="opacity-70" />
      </span>
    );
  }
  const myIdx = messages.findIndex((m) => m.id === messageId);
  const otherIdx = messages.findIndex((m) => m.id === otherLastReadId);
  // If we can't find one of them in the current page (very common for
  // messages older than the visible window), fall through to "read"
  // optimistically — we know the recipient's last-read is somewhere, and
  // showing a stale single-tick on a message they almost certainly did
  // read is the worse error.
  const isRead = myIdx === -1 || otherIdx === -1 || otherIdx >= myIdx;
  return isRead ? (
    <span aria-label="Read" title="Read" className="text-sky-400">
      <IconCheckDouble size={12} />
    </span>
  ) : (
    <span aria-label="Sent" title="Sent">
      <IconCheckDouble size={12} className="opacity-70" />
    </span>
  );
}
